import { useState } from 'react';
import { ImportResult } from '@/hooks/useImport';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ImportResultsProps {
  result: ImportResult;
  entityType: string;
  onClose: () => void;
  onImportAnother: () => void;
}

export function ImportResults({
  result,
  entityType,
  onClose,
  onImportAnother,
}: ImportResultsProps) {
  const [showErrors, setShowErrors] = useState(false);
  const hasErrors = result.errors.length > 0;
  const allFailed = result.success === 0 && result.failed > 0;

  return (
    <div className="space-y-6 py-4">
      {/* Result icon */}
      <div className="text-center">
        <div
          className={cn(
            'w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center',
            allFailed ? 'bg-destructive/10' : 'bg-green-100'
          )}
        >
          {allFailed ? (
            <svg
              className="w-8 h-8 text-destructive"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
        </div>
        <h3 className="text-lg font-semibold text-foreground">
          {allFailed ? 'Import Failed' : 'Import Complete'}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          {allFailed ? `Failed to import ${entityType}` : `Successfully imported ${entityType}`}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-center">
          <p className="text-3xl font-bold text-green-700">{result.success}</p>
          <p className="text-sm text-green-600">Imported</p>
        </div>
        <div
          className={cn(
            'p-4 rounded-lg text-center',
            result.failed > 0
              ? 'bg-red-50 border border-red-200'
              : 'bg-muted/50 border border-border'
          )}
        >
          <p
            className={cn(
              'text-3xl font-bold',
              result.failed > 0 ? 'text-red-700' : 'text-muted-foreground'
            )}
          >
            {result.failed}
          </p>
          <p
            className={cn('text-sm', result.failed > 0 ? 'text-red-600' : 'text-muted-foreground')}
          >
            Failed
          </p>
        </div>
      </div>

      {/* Error details */}
      {hasErrors && (
        <div className="space-y-2">
          <button
            onClick={() => setShowErrors(!showErrors)}
            className="w-full flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200 text-left hover:bg-amber-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4 text-amber-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span className="text-sm font-medium text-amber-800">
                {result.errors.length} error{result.errors.length !== 1 ? 's' : ''} occurred
              </span>
            </div>
            <svg
              className={cn(
                'w-4 h-4 text-amber-600 transition-transform',
                showErrors && 'rotate-180'
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {showErrors && (
            <div className="max-h-40 overflow-y-auto rounded-lg border border-border bg-muted/30">
              <div className="divide-y divide-border">
                {result.errors.slice(0, 20).map((error, index) => (
                  <div key={index} className="px-3 py-2 text-sm">
                    <span className="font-mono text-xs text-muted-foreground mr-2">
                      Row {error.row}:
                    </span>
                    <span className="text-foreground">{error.message}</span>
                  </div>
                ))}
                {result.errors.length > 20 && (
                  <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                    ... and {result.errors.length - 20} more errors
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-border">
        <Button variant="outline" onClick={onImportAnother}>
          Import Another
        </Button>
        <Button onClick={onClose}>Done</Button>
      </div>
    </div>
  );
}
